import { COUPON_VALIDATION, DISCOUNT_TYPES } from "constants/coupons";

const normalizeDiscountType = (discountType) => {
  const normalized = String(discountType || "").toLowerCase();
  return normalized === DISCOUNT_TYPES.FLAT
    ? DISCOUNT_TYPES.FLAT
    : DISCOUNT_TYPES.PERCENTAGE;
};

export const mapCouponInfoFromApi = (coupon) => {
  const rawPackageIds = coupon.packageIds ?? [];
  const rawPlatformIds = coupon.platformIds ?? [];
  const selectAllPackages = rawPackageIds.includes(
    COUPON_VALIDATION.ALL_PACKAGES_ID
  );
  const selectAllPlatforms = rawPlatformIds.includes(
    COUPON_VALIDATION.ALL_PLATFORMS_ID
  );
  const packageIds = rawPackageIds.filter(
    (id) => id !== COUPON_VALIDATION.ALL_PACKAGES_ID
  );
  const platformIds = rawPlatformIds.filter(
    (id) => id !== COUPON_VALIDATION.ALL_PLATFORMS_ID
  );

  return {
    id: coupon.couponCode,
    code: coupon.couponCode,
    category: coupon.couponCategory ?? "",
    remarks: coupon.remarks ?? "",
    discountType: normalizeDiscountType(
      coupon.discoutType ?? coupon.discountType
    ),
    discountValue: coupon.discountPercentOrAmount,
    maxAllowedAmount: coupon.maxAllowedDiscountAmount ?? "",
    effectiveDate: coupon.effectiveDate,
    expiryDate: coupon.expiryDate,
    totalCouponCount: coupon.maxAllowedCouponCount,
    totalUsageLimit: coupon.maxAllowedCouponCount,
    usedCount: coupon.totalCouponsUsed ?? 0,
    isApplicableMultipleTimes: coupon.applicableMultipleTimes ?? true,
    isApplicableForSingleUser: coupon.applicableForSingleUser ?? false,
    uamId: coupon.uamId ?? "",
    minimumPackageMrp: coupon.minimumAllowedMrp ?? 0,
    minimumCartMrp: coupon.minimumAllowedCartMrp ?? "",
    selectAllPackages,
    packageIds: selectAllPackages ? [] : packageIds,
    packagesCount: selectAllPackages ? undefined : packageIds.length,
    selectAllPlatforms,
    platformIds: selectAllPlatforms ? [] : platformIds,
    createdOn: coupon.createdDate,
    modifiedOn: coupon.modifiedDate,
    apiStatus: coupon.status,
  };
};

export const mapActiveCouponFromApi = (coupon) => ({
  id: coupon.coupon_code,
  code: coupon.coupon_code,
  category: coupon.coupon_category ?? "",
  discountValue: coupon.discount?.value ?? coupon.discount_value,
  discountType: normalizeDiscountType(
    coupon.discount?.type ?? coupon.discount_type
  ),
  effectiveDate: coupon.validity?.effective_date ?? coupon.effective_date,
  expiryDate: coupon.validity?.expiry_date ?? coupon.expiry_date,
  totalUsageLimit: coupon.usage?.total_count ?? coupon.created_coupons_count,
  usedCount: coupon.usage?.used_count ?? coupon.total_coupons_used ?? 0,
  createdOn: coupon.created_date,
});

export const getLocalIsoDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const toApiDate = (isoDate) => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  if (!year || !month || !day) return "";
  return `${day}/${month}/${year}`;
};

const toPackageManagementApiDate = (isoDate) => {
  if (!isoDate) return "";
  return String(isoDate).split("T")[0];
};

const toApiDiscountType = (discountType) =>
  discountType === DISCOUNT_TYPES.FLAT ? "FLAT" : "PERCENTAGE";

import { parsePackageIds } from "helpers/packageIds";

const parseCommaSeparatedIds = (value) => parsePackageIds(value);

export const buildCreateCouponPayload = (values) => {
  const payload = {
    code: values.code.trim(),
    category: values.category.trim(),
    remarks: values.remarks.trim(),
    validity: {
      effective_date: toApiDate(values.effectiveDate),
      expiry_date: toApiDate(values.expiryDate),
    },
    discount: {
      type: toApiDiscountType(values.discountType),
      value: Number(values.discountValue),
    },
    usage: {
      total_coupon_count: Number(values.totalCouponCount),
      is_applicable_multiple_times: Boolean(values.isApplicableMultipleTimes),
      is_applicable_for_single_user: Boolean(values.isApplicableForSingleUser),
    },
    eligibility: {
      minimum_package_mrp: Number(values.minimumPackageMrp),
    },
    applicable_packages: {
      select_all: Boolean(values.selectAllPackages),
      package_ids: values.selectAllPackages
        ? []
        : parseCommaSeparatedIds(values.packageIds),
    },
    applicable_platforms: {
      select_all: Boolean(values.selectAllPlatforms),
      platform_ids: values.selectAllPlatforms
        ? []
        : parseCommaSeparatedIds(values.platformIds),
    },
  };

  if (values.discountType === DISCOUNT_TYPES.PERCENTAGE) {
    payload.discount.max_allowed_amount = Number(values.maxAllowedAmount);
  }

  if (
    values.minimumCartMrp !== "" &&
    values.minimumCartMrp !== null &&
    values.minimumCartMrp !== undefined
  ) {
    payload.eligibility.minimum_cart_mrp = Number(values.minimumCartMrp);
  }

  if (values.isApplicableForSingleUser) {
    payload.usage.uam_id = Number(values.uamId);
  }

  return payload;
};

export const buildEditCouponPayload = (values) => {
  const payload = {
    remarks: (values.remarks ?? "").trim(),
    validity: {
      expiry_date: toPackageManagementApiDate(values.expiryDate),
    },
    usage: {
      operation: values.usageOperation || "INCREASE",
      count: Number(values.usageCountChange) || 0,
      is_applicable_multiple_times: Boolean(values.isApplicableMultipleTimes),
    },
    eligibility: {
      minimum_package_mrp: Number(values.minimumPackageMrp) || 0,
      minimum_cart_mrp: Number(values.minimumCartMrp) || 0,
    },
    applicable_packages: {
      select_all: Boolean(values.selectAllPackages),
      package_ids: values.selectAllPackages
        ? []
        : parseCommaSeparatedIds(values.packageIds),
    },
    applicable_platforms: {
      select_all: Boolean(values.selectAllPlatforms),
      platform_ids: values.selectAllPlatforms
        ? []
        : parseCommaSeparatedIds(values.platformIds),
    },
  };

  if (values.discountType === DISCOUNT_TYPES.PERCENTAGE) {
    payload.discount = {
      max_allowed_amount: Number(values.maxAllowedAmount) || 0,
    };
  }

  return payload;
};
